import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap, switchMap, map } from 'rxjs/operators';
import { MicroApiService } from '../constants/micro-api.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { checkContestDraft, readDraft, stampDraft } from './ai-draft-check';

/** What the generator takes besides the prompt. `goal` and `prize_budget` are E1.6. */
export interface ContestBrief {
  country?: string;
  audience?: string;
  start_date?: string;
  end_date?: string;
  language?: string;
  prize_budget?: number;
  goal?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiInsightService {
  private baseUrl = environment.Ai_insight_apiUrl;
  private apiUrl = `${this.baseUrl}/api/predictive_engagement`;
  private chatbotUrl = `${this.baseUrl}/api/ask_to_bot`;

  private readonly PERMANENT_TOKEN = '9dX$44A[~K10';

  // ChatBolt Dashboard API
  private chatboltBaseUrl = environment.chatboltApiUrl;
  private chatboltApiKey = environment.chatboltKeys.assistant;

  // IndexedDB cache — 2 hour TTL
  private readonly DB_NAME = 'luqta_ai_cache';
  private readonly STORE_NAME = 'api_cache';
  private readonly CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours in ms

  constructor(private http: HttpClient, private translate: TranslateService, private micro: MicroApiService) {
    this.initDb();
  }

  private dbReady: Promise<IDBDatabase> | null = null;

  private initDb(): void {
    this.dbReady = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async getFromCache(key: string): Promise<any | null> {
    try {
      const db = await this.dbReady!;
      return new Promise((resolve) => {
        const tx = db.transaction(this.STORE_NAME, 'readonly');
        const store = tx.objectStore(this.STORE_NAME);
        const req = store.get(key);
        req.onsuccess = () => {
          const record = req.result;
          if (record && (Date.now() - record.timestamp) < this.CACHE_TTL) {
            resolve(record.data);
          } else {
            resolve(null); // expired or not found
          }
        };
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  private async saveToCache(key: string, data: any): Promise<void> {
    try {
      const db = await this.dbReady!;
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      store.put({ key, data, timestamp: Date.now() });
    } catch { /* silent */ }
  }

  /**
   * Only a usable answer is cached. A failed call or an AI reply that is not
   * structured data would otherwise be served back for the whole TTL.
   */
  private isCacheable(response: any): boolean {
    if (!response || typeof response !== 'object' || Object.keys(response).length === 0) return false;
    if (response.success === false || response.ai_error) return false;
    if ('ai_result' in response) {
      const ai = response.ai_result;
      return !!ai && typeof ai === 'object';
    }
    return true;
  }

  /**
   * Wrap an API call with IndexedDB cache
   * Returns cached data if < 2 hours old, otherwise calls API and caches result
   */
  private withCache(cacheKey: string, apiCall: Observable<any>): Observable<any> {
    return new Observable(observer => {
      this.getFromCache(cacheKey).then(cached => {
        if (cached) {
          observer.next(cached);
          observer.complete();
        } else {
          apiCall.subscribe({
            next: (response) => {
              if (this.isCacheable(response)) {
                this.saveToCache(cacheKey, response);
              }
              observer.next(response);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        }
      });
    });
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${this.PERMANENT_TOKEN}`,
      'Content-Type': 'application/json'
    });
  }

  isQuotaExceededError(error: unknown): boolean {
    const err = error as HttpErrorResponse | any;
    const message = String(err?.error?.message || err?.message || '').toLowerCase();
    const blockedHook = String(err?.error?.blocked_by_hook || '').toLowerCase();

    return (
      err?.status === 403 &&
      (
        message.includes('monthly ai token quota is exhausted') ||
        message.includes('token quota') ||
        message.includes('quota is exhausted') ||
        message.includes('quota exhausted') ||
        blockedHook.includes('token quota guard')
      )
    );
  }

  isAllowanceNotConfiguredError(error: unknown): boolean {
    const err = error as HttpErrorResponse | any;
    const message = String(err?.error?.message || err?.message || '').toLowerCase();

    return (
      message.includes('no ai allowance') ||
      message.includes('ai allowance is not configured') ||
      message.includes('allowance not configured')
    );
  }

  getFriendlyAiErrorMessage(error: unknown, fallback?: string): string {
    const err = error as HttpErrorResponse | any;

    if (this.isQuotaExceededError(err)) {
      return this.translate.instant('ai-insight.quota-exceeded-message');
    }

    if (this.isAllowanceNotConfiguredError(err)) {
      return this.translate.instant('ai-insight.no-allowance-message');
    }

    // The draft check refused the draft (E1.1 / E1.6): say why, in the user's language.
    const code = err?.error?.code;
    if (code === 'AI_DRAFT_INVALID' || code === 'AI_DRAFT_OVER_BUDGET') {
      const errors: string[] = Array.isArray(err?.error?.errors) ? err.error.errors : [];
      const head = this.translate.instant(
        code === 'AI_DRAFT_OVER_BUDGET' ? 'ai-insight.draft-over-budget' : 'ai-insight.draft-invalid',
      );
      return errors.length ? `${head} ${errors.slice(0, 3).join(' ')}` : head;
    }

    const message = err?.error?.message || err?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    return fallback || this.translate.instant('ai-insight.generic-error-message');
  }

  askToBot(userQuery: string): Observable<any> {

    const headers = this.getHeaders();
    const payload = {
      user_query: userQuery
    };


    return this.http.post<any>(this.chatbotUrl, payload, { headers }).pipe(
      tap((response) => {
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Chatbot API error:', error.status, error.message);
        console.error('Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get predictive engagement data from the API
   * @param clientId - The client ID
   * @param clientName - The client name
   * @param isRetry - Flag to prevent infinite retry loops
   */
  getPredictiveEngagement(clientId: string, clientName: string, isRetry: boolean = false): Observable<any> {
    const headers = this.getHeaders();

    const finalClientName = clientName || 'Luqta Admin';
    const finalClientId = clientId || '';

    // ✅ Payload with BOTH client_id and client_name
    const payload = {
      client_id: finalClientId,
      client_name: finalClientName
    };


    return this.http.post<any>(this.apiUrl, payload, { headers }).pipe(
      tap((response) => {
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Predictive engagement error:', error.status, error.message);
        console.error('Error details:', error.error);

        if (error.status === 422 && !isRetry && clientName !== 'Luqta Admin') {
          return this.getPredictiveEngagement('', 'Luqta Admin', true);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Get user segments data from the API
   * @param clientName - The client name to fetch segments for
   * @param isRetry - Flag to prevent infinite retry loops
   */
  /**
   * Get AI dashboard insights from ChatBolt API
   * @param companyId - The company ID to fetch insights for
   */
  getDashboardInsights(companyId: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': this.chatboltApiKey,
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/api-builder/public/query/dashboard-28jhy`,
      { company_id: companyId },
      { headers }
    ).pipe(
      tap((response) => { }),
      catchError((error: HttpErrorResponse) => {
        console.error('ChatBolt Dashboard API error:', error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * Generate contest via ChatBolt AI Endpoint (execute-by-name)
   * Proxy: /api/v1/ai-endpoints → localhost:3000
   */
  /**
   * Campaign ideas for this business. Cached per company for the cache TTL, so
   * opening "Create with AI" again does not pay for (and wait on) a fresh AI
   * call each time; `fresh` skips the cache for the modal's refresh button.
   *
   * `contextToken` is the assistant's signed context (action 2486). ChatBolt
   * then uses the company it vouches for, whatever the body says — the stored
   * user details can carry an id that is not the organisation, and the plan
   * check refused those calls as "no active paid plan".
   */
  fetchContestSuggestions(companyId?: string | number | null, fresh = false, contextToken?: string | null): Observable<any> {
    let headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.endpoints,
      'Content-Type': 'application/json'
    });
    if (contextToken) headers = headers.set('x-context-token', contextToken);

    const apiCall = this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/ai-endpoints/public/execute-by-name/luqta_ai_contest_suggestions-pyask`,
      {
        company_id: companyId ?? null,
      },
      { headers },
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('AI Contest Suggestions error:', error.status, error.message);
        return throwError(() => error);
      })
    );

    if (fresh) return apiCall;
    const cacheKey = this.suggestionsCacheKey(companyId);
    return new Observable(observer => {
      this.getFromCache(cacheKey).then(cached => {
        if (cached) {
          observer.next(cached);
          observer.complete();
          return;
        }
        apiCall.subscribe(observer);
      });
    });
  }

  /**
   * Stored by the caller only once the answer has parsed into real ideas — an
   * AI reply that is prose rather than a list would otherwise be served back
   * for the whole TTL.
   */
  cacheContestSuggestions(companyId: string | number | null | undefined, response: any): void {
    this.saveToCache(this.suggestionsCacheKey(companyId), response);
  }

  private suggestionsCacheKey(companyId: string | number | null | undefined): string {
    return `contest_suggestions_${companyId ?? 'none'}`;
  }

  /**
   * @param brief Values already agreed with the client. The endpoint accepts
   *   these as first-class inputs, so passing them keeps the generated contest
   *   faithful to what was confirmed — relying on the prose prompt alone lets
   *   the model re-derive a country or a budget and quietly drift.
   */
  generateContest(
    prompt: string,
    companyId?: string | number | null,
    brief?: ContestBrief,
  ): Observable<any> {
    // Gateway first (2377): it checks the plan's AI entitlement and points, then calls ChatBolt.
    // Only when the gateway itself can't reach ChatBolt do we fall back to the direct call below;
    // plan/quota refusals are real answers and go straight to the caller.
    const body = {
      prompt,
      country: brief?.country ?? '',
      audience: brief?.audience ?? '',
      language: brief?.language ?? '',
      start_date: brief?.start_date ?? '',
      end_date: brief?.end_date ?? '',
      prize_budget: Number(brief?.prize_budget) > 0 ? Number(brief!.prize_budget) : 0,
      goal: String(brief?.goal ?? '').trim().slice(0, 500),
    };
    return this.micro.call('CONTEST_AI_GENERATE', body).pipe(
      map((data) => ({ data })),
      catchError((err) => {
        const code = err?.error?.code;
        if (code === 'AI_NOT_CONFIGURED' || code === 'AI_UNREACHABLE' || !err?.error?.code) {
          return this.generateContestDirect(prompt, companyId, brief);
        }
        return throwError(() => err);
      }),
    );
  }

  /** Direct ChatBolt call — the fallback when the gateway's AI isn't configured/reachable. */
  private generateContestDirect(
    prompt: string,
    companyId?: string | number | null,
    brief?: ContestBrief,
  ): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.endpoints,
      'Content-Type': 'application/json'
    });

    // The goal and budget are said in the prompt too, as the gateway does: the endpoint may not
    // read the new body fields.
    const goal = String(brief?.goal ?? '').trim().slice(0, 500);
    const budget = Number(brief?.prize_budget) > 0 ? Number(brief!.prize_budget) : 0;
    const enrichedPrompt = [
      prompt,
      goal ? `Campaign goal: ${goal}. Design the mechanic and rewards for this goal.` : '',
      budget ? `The prizes' total value must not exceed ${budget}.` : '',
    ].filter(Boolean).join('\n\n');

    return this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/ai-endpoints/public/execute-by-name/create_contest-kgeyw`,
      {
        prompt: enrichedPrompt,
        company_id: companyId ?? null,
        country: brief?.country ?? '',
        audience: brief?.audience ?? '',
        end_date: brief?.end_date ?? '',
        language: brief?.language ?? '',
        start_date: brief?.start_date ?? '',
        prize_budget: budget,
        goal,
      },
      { headers },
    ).pipe(
      map((response) => this.checkedDirectDraft(response, goal, budget)),
      catchError((error: HttpErrorResponse) => {
        console.error('AI Contest Generation error:', error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * The fallback's draft, held to the same check as the gateway's (E1.1 / E1.6): one that fails
   * the data-format check, or is above the budget, is refused with the server's codes and never
   * shown. A passing one comes back as `{ data }`, stamped like the server's.
   */
  private checkedDirectDraft(response: any, goal: string, budget: number): { data: any } {
    const result = response?.result || response?.response?.result || response || {};
    if (result?.status === 403 || result?.blocked_by_hook) {
      throw { error: { code: 'AI_QUOTA', message: String(result?.message || '') } };
    }
    const draft = readDraft(result?.data ?? result);
    const check = checkContestDraft(draft, { budget });
    if (!check.ok) {
      const over = check.overBudget && check.errors.length === 1;
      throw { error: { code: over ? 'AI_DRAFT_OVER_BUDGET' : 'AI_DRAFT_INVALID', errors: check.errors } };
    }
    return { data: stampDraft(draft, check, { goal, budget, attempts: 1 }) };
  }

  /**
   * Writes the questions for one quiz level (ChatBolt `generate_quiz`). Answers in
   * the shape QUIZ_UPSERT takes: { quiz_title, description, questions: [{ question,
   * arabic_question, question_order, options: [{ option_text, arabic_option_text,
   * is_correct }] }] }.
   *
   * `quiz_brief` is what the client said about this quiz in the chat — a topic, or
   * questions they dictated — and the endpoint follows it over its own choices.
   */
  generateQuiz(input: {
    level_name: string;
    level_description: string;
    campaign_name: string;
    campaign_details: string;
    business_type: string;
    language: string;
    question_count: number;
    quiz_brief: string;
  }): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.endpoints,
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/ai-endpoints/public/execute-by-name/generate_quiz-qz1x7`,
      input,
      { headers },
    ).pipe(
      map((response) => response?.data ?? response),
      catchError((error: HttpErrorResponse) => {
        console.error('AI Quiz Generation error:', error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  getPredictions(companyId: string | number = 0): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.insights,
      'Content-Type': 'application/json'
    });

    const apiCall = this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/api-builder/public/query/ai-insight-prediction-18nuy`,
      { company_id: companyId },
      { headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Predictions API error:', error.status, error.message);
        return throwError(() => error);
      })
    );

    return this.withCache(`predictions_v2_${companyId}`, apiCall);
  }

  getScenarios(companyId: string | number = 0): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.insights,
      'Content-Type': 'application/json'
    });

    const apiCall = this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/api-builder/public/query/ai-prediction-scenario-0swx1`,
      { company_id: companyId },
      { headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Scenarios API error:', error.status, error.message);
        return throwError(() => error);
      })
    );

    return this.withCache(`scenarios_v2_${companyId}`, apiCall);
  }

  getRisksAnalysis(companyId: string | number = 0): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.insights,
      'Content-Type': 'application/json'
    });

    const apiCall = this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/api-builder/public/query/ai-risks-analysis-predictions-fa0jw`,
      { company_id: companyId },
      { headers }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Risks API error:', error.status, error.message);
        return throwError(() => error);
      })
    );

    return this.withCache(`risks_${companyId}`, apiCall);
  }

  getUserSegments(companyId: string | number = 0): Observable<any> {
    const headers = new HttpHeaders({
      'x-api-key': environment.chatboltKeys.insights,
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(
      `${this.chatboltBaseUrl}/api/v1/api-builder/public/query/ai-insight-segment-cadv2`,
      { company_id: companyId },
      { headers }
    ).pipe(
      tap((response) => { }),
      catchError((error: HttpErrorResponse) => {
        console.error('User segments API error:', error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  // Together AI — FLUX.1-schnell (fast, high quality, reliable)
  private togetherApiUrl = 'https://api.together.xyz/v1/images/generations';
  private togetherApiKey = 'tgp_v1_fBon9onW9xD6WIFEPguFZAt7qcJk7LIW4WtPEMMaWps';

  /**
   * Generate AI image via Together AI FLUX model
   * Returns Observable that emits an image URL
   */
  generateAiImage(prompt: string): Observable<string> {
    return new Observable<string>(observer => {
      fetch(this.togetherApiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.togetherApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'black-forest-labs/FLUX.1-schnell',
          prompt,
          n: 1,
          width: 1024,
          height: 768,
        }),
      })
      .then(response => {
        if (!response.ok) throw new Error(`Together API: ${response.status}`);
        return response.json();
      })
      .then((result: any) => {
        const imageUrl = result?.data?.[0]?.url;
        if (imageUrl) {
          observer.next(imageUrl);
          observer.complete();
        } else {
          observer.error('No image URL in response');
        }
      })
      .catch(() => {
        // Silent fail — SVG stays
        observer.error('image generation failed');
      });
    });
  }

  /**
   * Build AI prompt for contest banner — uses name + description for context
   */
  buildBannerPrompt(contestName: string, contestDetails: string): string {
    const name = contestName.replace(/[^a-zA-Z0-9 ,.'&-]/g, '').substring(0, 40);
    // Extract theme keywords from description
    const theme = this.extractThemeKeywords(contestDetails);
    return `Stunning wide banner photograph, ${theme}, beautiful cinematic lighting, vibrant rich colors, professional advertising campaign style, ultra high quality, 8k, photorealistic, ${name} theme, bokeh background, absolutely no text no letters no words no watermark`;
  }

  /**
   * Build AI prompt for prize — generates realistic product photo
   */
  buildPrizePrompt(prizeName: string, prizeDescription: string): string {
    const name = prizeName.replace(/[^a-zA-Z0-9 ,.'&-]/g, '').substring(0, 60);
    const desc = (prizeDescription || '').replace(/[^a-zA-Z0-9 ,.'&-]/g, '').substring(0, 40);
    // Detect prize type for better prompts
    const lowerName = prizeName.toLowerCase();
    if (lowerName.includes('iphone') || lowerName.includes('samsung') || lowerName.includes('phone') || lowerName.includes('galaxy')) {
      return `Professional product photography, ${name}, latest smartphone, sleek modern design, floating in air, soft gradient background, studio lighting, commercial advertisement, ultra realistic, 8k detail`;
    }
    if (lowerName.includes('gift card') || lowerName.includes('voucher') || lowerName.includes('card')) {
      return `Elegant premium gift card design, ${name}, golden accents, luxury feel, floating with soft shadow, clean minimal background, professional commercial photo, high quality`;
    }
    if (lowerName.includes('airpod') || lowerName.includes('headphone') || lowerName.includes('watch') || lowerName.includes('ipad') || lowerName.includes('laptop')) {
      return `Professional product photography, ${name}, premium tech gadget, floating elegantly, soft studio lighting, clean gradient background, commercial ad style, ultra realistic 8k`;
    }
    if (lowerName.includes('cash') || lowerName.includes('money') || lowerName.includes('sar') || lowerName.includes('usd')) {
      return `Luxurious golden trophy with cash prize, ${name}, elegant award ceremony style, sparkling gold confetti, premium dark background, professional photography, celebration`;
    }
    return `Professional product showcase, ${name}, ${desc}, elegant presentation, soft studio lighting, premium feel, clean background, commercial advertisement photography, ultra realistic, high quality`;
  }

  /**
   * Build AI prompt for level — matches the level type and description
   */
  buildLevelPrompt(levelName: string, levelDescription: string, levelType: string): string {
    const name = levelName.replace(/[^a-zA-Z0-9 ,.'&-]/g, '').substring(0, 40);
    const desc = (levelDescription || '').replace(/[^a-zA-Z0-9 ,.'&-]/g, '').substring(0, 60);

    if (levelType === 'image' || levelType === 'photo') {
      return `Beautiful photography scene, ${name}, ${desc}, stunning composition, natural lighting, vibrant colors, professional photograph, cinematic, 8k quality, no text no watermark`;
    }
    if (levelType === 'qr') {
      return `Modern lifestyle scene of person scanning QR code at elegant store, ${name}, bright clean environment, friendly atmosphere, professional photography, natural lighting`;
    }
    if (levelType === 'quiz') {
      return `Fun colorful quiz challenge visual, ${name}, bright playful design, question marks, knowledge theme, engaging vibrant illustration, modern digital art style`;
    }
    // text type
    return `Creative writing inspiration scene, ${name}, ${desc}, warm inviting atmosphere, artistic photography, beautiful bokeh, cinematic mood lighting, professional quality`;
  }

  /**
   * Extract meaningful theme keywords from contest description
   */
  private extractThemeKeywords(text: string): string {
    if (!text) return 'celebration, competition, excitement';
    const clean = text.replace(/[^a-zA-Z ]/g, ' ').toLowerCase();
    const themeWords = ['travel', 'photo', 'food', 'cooking', 'tech', 'gaming', 'sport', 'fitness',
      'fashion', 'beauty', 'art', 'music', 'nature', 'adventure', 'shopping', 'car', 'home',
      'decor', 'health', 'education', 'family', 'kids', 'pet', 'ocean', 'desert', 'city',
      'mountain', 'beach', 'garden', 'coffee', 'restaurant', 'luxury', 'creative', 'writing'];
    const found = themeWords.filter(w => clean.includes(w));
    const theme = found.length > 0 ? found.slice(0, 3).join(', ') : 'celebration, competition';
    return theme + ', excitement, premium quality';
  }
}

